import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ChevronDown, RotateCcw } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 24;
const NOTE_SUGGESTIONS = ["Vainilla", "Sándalo", "Oud", "Rosa", "Ámbar", "Bergamota", "Vetiver", "Jazmín", "Almizcle", "Cedro"];
const OCCASIONS = [
  { key: "diario",     label: "Diario / Casual" },
  { key: "oficina",    label: "Oficina" },
  { key: "noche",      label: "Noche / Cena" },
  { key: "romantico",  label: "Romántico" },
  { key: "formal",     label: "Formal / Eventos" },
  { key: "deporte",    label: "Deporte / Fresh" },
  { key: "verano",     label: "Verano / Playa" },
  { key: "invierno",   label: "Invierno" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ brands: [], families: [], concentrations: [], genders: [] });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 400]);

  const family = params.get("family") || "";
  const brand = params.get("brand") || "";
  const concentration = params.get("concentration") || "";
  const gender = params.get("gender") || "";
  const sort = params.get("sort") || "relevance";
  const q = params.get("q") || "";
  const badge = params.get("badge") || "";
  const note = params.get("note") || "";
  const occasion = params.get("occasion") || "";
  const category = params.get("category") || "";

  const [noteInput, setNoteInput] = useState(note);

  const baseQuery = useMemo(() => {
    const query = {};
    if (family) query.family = family;
    if (brand) query.brand = brand;
    if (concentration) query.concentration = concentration;
    if (gender) query.gender = gender;
    if (badge) query.badge = badge;
    if (q) query.q = q;
    if (note) query.note = note;
    if (occasion) query.occasion = occasion;
    if (category) query.category = category;
    if (sort) query.sort = sort;
    query.min_price = priceRange[0];
    query.max_price = priceRange[1];
    return query;
  }, [family, brand, concentration, gender, sort, q, badge, note, occasion, category, priceRange]);

  useEffect(() => {
    api.get("/products/facets").then(({ data }) => setFacets(data));
  }, []);

  // Reset when filters change
  useEffect(() => {
    setLoading(true);
    setProducts([]);
    Promise.all([
      api.get("/products", { params: { ...baseQuery, skip: 0, limit: PAGE_SIZE } }),
      api.get("/products/count", { params: baseQuery }),
    ]).then(([r1, r2]) => {
      setProducts(r1.data);
      setTotal(r2.data.count);
    }).catch(() => {
      setProducts([]);
      setTotal(0);
    }).finally(() => setLoading(false));
  }, [baseQuery]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || products.length >= total) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/products", { params: { ...baseQuery, skip: products.length, limit: PAGE_SIZE } });
      setProducts((prev) => [...prev, ...data]);
    } finally {
      setLoadingMore(false);
    }
  }, [baseQuery, products.length, total, loading, loadingMore]);

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "500px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [loadMore]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const applyNote = () => updateParam("note", noteInput.trim());

  const hasAnyFilter = useMemo(
    () => !!(family || brand || concentration || gender || badge || note || occasion || category || q ||
             priceRange[0] > 0 || priceRange[1] < 400),
    [family, brand, concentration, gender, badge, note, occasion, category, q, priceRange]
  );

  const resetAll = () => {
    setParams({});
    setPriceRange([0, 400]);
    setNoteInput("");
  };

  const activeFilters = useMemo(() => {
    const list = [];
    if (family) list.push({ k: "family", v: family });
    if (brand) list.push({ k: "brand", v: brand });
    if (concentration) list.push({ k: "concentration", v: concentration });
    if (gender) list.push({ k: "gender", v: gender });
    if (badge) list.push({ k: "badge", v: badge });
    if (note) list.push({ k: "note", v: `Nota: ${note}` });
    if (occasion) {
      const o = OCCASIONS.find((x) => x.key === occasion);
      list.push({ k: "occasion", v: o ? o.label : occasion });
    }
    if (category) {
      const CATEGORY_LABELS = { arab: "Árabes", kids: "Kids", gift_set: "Sets de Regalo", new: "Recién Llegados" };
      list.push({ k: "category", v: CATEGORY_LABELS[category] || category });
    }
    return list;
  }, [family, brand, concentration, gender, badge, note, occasion, category]);

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12">
      {/* Breadcrumb */}
      <nav className="font-mono-caps text-xs text-muted-foreground mb-8" data-testid="shop-breadcrumb">
        <Link to="/" className="hover:text-foreground">Inicio</Link> · <span className="text-foreground">Boutique</span>
        {family && <> · <span className="text-foreground">{family}</span></>}
      </nav>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight">
            {family ? `${family.charAt(0).toUpperCase() + family.slice(1)}s` : "Todos los perfumes"}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">{loading ? "Cargando…" : `${total.toLocaleString()} fragancias`}</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-mono-caps text-muted-foreground">Ordenar</span>
          <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
            <SelectTrigger className="w-56 rounded-none border-black" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="relevance">Relevancia</SelectItem>
              <SelectItem value="bestsellers">Más vendidos</SelectItem>
              <SelectItem value="newest">Novedades</SelectItem>
              <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
              <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active filters */}
      {activeFilters.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-8" data-testid="active-filters">
          {activeFilters.map((f) => (
            <button
              key={`${f.k}-${f.v}`}
              onClick={() => updateParam(f.k, "")}
              className="font-mono-caps text-xs border border-navy px-3 py-2 hover:bg-navy hover:text-cream transition-colors"
              data-testid={`clear-filter-${f.k}`}
            >
              {f.v} ×
            </button>
          ))}
          <button onClick={resetAll} className="font-mono-caps text-xs underline text-muted-foreground" data-testid="clear-all-filters">
            Limpiar todo
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-12 gap-8 md:gap-12">
        {/* Filters */}
        <aside className="md:col-span-3 md:sticky md:top-32 self-start" data-testid="shop-filters">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono-caps">Filtros</div>
            <button
              onClick={resetAll}
              disabled={!hasAnyFilter}
              className="flex items-center gap-1 font-mono-caps text-[10px] text-[#8B0000] hover:text-navy disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
              data-testid="reset-filters-btn"
              title="Restablecer todos los filtros"
            >
              <RotateCcw strokeWidth={1.5} className="h-3 w-3" /> Restablecer
            </button>
          </div>
          <Accordion type="multiple" defaultValue={[]}>
            <AccordionItem value="note" className="border-t border-border">
              <AccordionTrigger className="font-mono-caps text-xs">Notas olfativas</AccordionTrigger>
              <AccordionContent>
                <div className="flex gap-2">
                  <input
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyNote()}
                    placeholder="ej. Vainilla, Sándalo…"
                    className="flex-1 border border-border p-2 text-sm focus:border-navy outline-none"
                    data-testid="filter-note-input"
                  />
                  <button
                    onClick={applyNote}
                    className="border border-navy px-3 py-2 text-xs font-mono-caps hover:bg-navy hover:text-cream transition-colors"
                    data-testid="filter-note-apply"
                  >
                    OK
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {NOTE_SUGGESTIONS.map((n) => (
                    <button
                      key={n}
                      onClick={() => { setNoteInput(n); updateParam("note", n); }}
                      className="border border-border px-2 py-1 text-[10px] font-mono-caps hover:border-navy hover:text-navy transition-colors"
                      data-testid={`note-suggestion-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="occasion" className="border-t border-border">
              <AccordionTrigger className="font-mono-caps text-xs">Ocasión de uso</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {OCCASIONS.map((o) => (
                  <label key={o.key} className="flex items-center gap-3 text-sm cursor-pointer">
                    <Checkbox
                      checked={occasion === o.key}
                      onCheckedChange={() => updateParam("occasion", occasion === o.key ? "" : o.key)}
                      className="rounded-none"
                      data-testid={`filter-occasion-${o.key}`}
                    />
                    {o.label}
                  </label>
                ))}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="price" className="border-t border-border">
              <AccordionTrigger className="font-mono-caps text-xs">Rango de precio</AccordionTrigger>
              <AccordionContent>
                <Slider
                  min={0}
                  max={400}
                  step={10}
                  value={priceRange}
                  onValueChange={setPriceRange}
                  className="my-6"
                  data-testid="filter-price"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>${priceRange[0]}</span>
                  <span>${priceRange[1]}</span>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="family" className="border-t border-border">
              <AccordionTrigger className="font-mono-caps text-xs">Familia olfativa</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {facets.families.map((f) => (
                  <label key={f} className="flex items-center gap-3 text-sm cursor-pointer">
                    <Checkbox checked={family === f} onCheckedChange={() => updateParam("family", family === f ? "" : f)} className="rounded-none" data-testid={`filter-family-${f}`} />
                    <span className="capitalize">{f}</span>
                  </label>
                ))}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="brand">
              <AccordionTrigger className="font-mono-caps text-xs">Marca</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {facets.brands.map((b) => (
                  <label key={b} className="flex items-center gap-3 text-sm cursor-pointer">
                    <Checkbox checked={brand === b} onCheckedChange={() => updateParam("brand", brand === b ? "" : b)} className="rounded-none" data-testid={`filter-brand-${b}`} />
                    {b}
                  </label>
                ))}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="concentration">
              <AccordionTrigger className="font-mono-caps text-xs">Concentración</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {facets.concentrations.map((c) => (
                  <label key={c} className="flex items-center gap-3 text-sm cursor-pointer">
                    <Checkbox checked={concentration === c} onCheckedChange={() => updateParam("concentration", concentration === c ? "" : c)} className="rounded-none" data-testid={`filter-conc-${c}`} />
                    {c}
                  </label>
                ))}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="gender">
              <AccordionTrigger className="font-mono-caps text-xs">Género</AccordionTrigger>
              <AccordionContent className="space-y-3">
                {facets.genders.map((g) => (
                  <label key={g} className="flex items-center gap-3 text-sm cursor-pointer">
                    <Checkbox checked={gender === g} onCheckedChange={() => updateParam("gender", gender === g ? "" : g)} className="rounded-none" data-testid={`filter-gender-${g}`} />
                    <span className="capitalize">{g}</span>
                  </label>
                ))}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          {hasAnyFilter && (
            <button
              onClick={resetAll}
              className="mt-6 w-full flex items-center justify-center gap-2 border border-[#8B0000] text-[#8B0000] py-3 font-mono-caps text-xs hover:bg-[#8B0000] hover:text-cream transition-colors"
              data-testid="reset-filters-btn-bottom"
            >
              <RotateCcw strokeWidth={1.5} className="h-3.5 w-3.5" /> Limpiar filtros
            </button>
          )}
        </aside>

        {/* Grid */}
        <div className="md:col-span-9">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-serif text-3xl">Ningún resultado</p>
              <p className="text-muted-foreground mt-2">Prueba a ajustar los filtros.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8" data-testid="product-grid">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i % PAGE_SIZE} />)}
              </div>
              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="h-16 mt-8 flex items-center justify-center font-mono-caps text-xs text-muted-foreground" data-testid="shop-sentinel">
                {loadingMore ? "Cargando más…" : products.length < total ? "Desplázate para ver más" : `— ${total.toLocaleString()} resultados —`}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
